import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { Label as FlowLabel } from 'flowbite-react';
import { Radio as FlowRadio, Checkbox as FlowCheckbox } from 'flowbite-react';

import Input from 'components/input/Input';
import { ListTable } from 'components/list';

/* Komponen dasar lokal (HTML/Tailwind murni) untuk API yang tidak tersedia di flowbite-react. */

/* ============ TextInput (dengan label, meniru API material-tailwind) ============ */
export const TextInput = React.forwardRef(function TextInput(
  {
    size: _size,
    sizing: _sizing,
    shadow: _shadow,
    color: _color,
    variant: _variant,
    theme: _theme,
    ...props
  },
  ref
) {
  return <Input {...props} ref={ref} />;
});

/* ============ Textarea (dengan label) ============ */
export const Textarea = React.forwardRef(function Textarea({ color: _color, ...props }, ref) {
  return <Input {...props} as='textarea' ref={ref} />;
});

/* ============ Radio (dengan label) ============ */
export const Radio = React.forwardRef(function Radio(
  { label, color = 'blue-gray', className, containerProps, ...props },
  ref
) {
  return (
    <div className={containerProps?.className}>
      <div className='flex items-center gap-2'>
        <FlowRadio ref={ref} color={color} className={className} {...props} />
        {label && (
          <FlowLabel htmlFor={props.id} className='text-sm text-gray-700'>
            {label}
          </FlowLabel>
        )}
      </div>
    </div>
  );
});

/* ============ Checkbox (dengan label) ============ */
export const Checkbox = React.forwardRef(function Checkbox(
  { label, color = 'blue-gray', className, ...props },
  ref
) {
  return (
    <div className='flex items-center gap-2'>
      <FlowCheckbox ref={ref} color={color} className={className} {...props} />
      {label && (
        <FlowLabel htmlFor={props.id} className='text-sm text-gray-700'>
          {label}
        </FlowLabel>
      )}
    </div>
  );
});

/* ============ Card ============ */
export const Card = ({ className, children, ...props }) => (
  <div className={`bg-white rounded-lg shadow-md ${className || ''}`} {...props}>
    {children}
  </div>
);

export const CardBody = ({ className, children, ...props }) => (
  <div className={className} {...props}>
    {children}
  </div>
);

export const CardHeader = ({ className, children, ...props }) => (
  <div className={className} {...props}>
    {children}
  </div>
);

export const CardFooter = ({ className, children, ...props }) => (
  <div className={className} {...props}>
    {children}
  </div>
);

/* ============ Typography ============ */
const typTag = {
  h1: 'h1',
  h2: 'h2',
  h3: 'h3',
  h4: 'h4',
  h5: 'h5',
  h6: 'h6',
  lead: 'p',
  paragraph: 'p',
  small: 'small',
};
const typSize = {
  h1: 'text-5xl',
  h2: 'text-4xl',
  h3: 'text-3xl',
  h4: 'text-2xl',
  h5: 'text-xl',
  h6: 'text-lg',
  lead: 'text-xl',
  paragraph: 'text-base',
  small: 'text-sm',
};
const typColor = {
  'blue-gray': 'text-blue-gray-700',
  gray: 'text-gray-700',
  blue: 'text-blue-500',
  red: 'text-red-500',
  green: 'text-green-500',
  amber: 'text-amber-500',
  cyan: 'text-cyan-500',
  white: 'text-white',
  black: 'text-black',
};
export const Typography = ({ variant = 'paragraph', color, className, children, ...props }) => {
  const Tag = typTag[variant] || 'p';
  return (
    <Tag
      className={`${typSize[variant] || ''} ${typColor[color] || ''} ${className || ''}`}
      {...props}
    >
      {children}
    </Tag>
  );
};

/* ============ IconButton ============ */
export const IconButton = ({
  variant = 'filled',
  color = 'blue-gray',
  size = 'md',
  className,
  children,
  ...props
}) => {
  const sz = { sm: 'p-1.5', md: 'p-2', lg: 'p-3' }[size] || 'p-2';
  const filled = {
    'blue-gray': 'bg-blue-gray-500 text-white hover:bg-blue-gray-600',
    blue: 'bg-blue-500 text-white hover:bg-blue-600',
    red: 'bg-red-500 text-white hover:bg-red-600',
    green: 'bg-green-500 text-white hover:bg-green-600',
    amber: 'bg-amber-500 text-white hover:bg-amber-600',
    cyan: 'bg-cyan-500 text-white hover:bg-cyan-600',
    gray: 'bg-gray-500 text-white hover:bg-gray-600',
    white: 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200',
  };
  const cls =
    variant === 'text' ? 'text-gray-500 hover:text-gray-800' : filled[color] || filled['blue-gray'];
  return (
    <button
      type='button'
      className={`inline-flex items-center justify-center rounded-lg transition-colors ${sz} ${cls} ${className || ''}`}
      {...props}
    >
      {children}
    </button>
  );
};

/* ============ List ============ */
export const List = ({ className, children, ...props }) => (
  <ul className={className} {...props}>
    {children}
  </ul>
);
export const ListItem = ({ className, children, ...props }) => (
  <li className={className} {...props}>
    {children}
  </li>
);
export const ListItemPrefix = ({ className, children, ...props }) => (
  <span className={className} {...props}>
    {children}
  </span>
);
export const ListItemSuffix = ({ className, children, ...props }) => (
  <span className={className} {...props}>
    {children}
  </span>
);

/* ============ Collapse ============ */
export const Collapse = ({ open, className, children, ...props }) =>
  open ? (
    <div className={className} {...props}>
      {children}
    </div>
  ) : null;

/* ============ FormControl ============ */
export const FormControl = ({ className, children, ...props }) => (
  <div className={className} {...props}>
    {children}
  </div>
);

/* ============ ButtonGroup ============ */
export const ButtonGroup = ({ size: _size, variant: _variant, className, children, ...props }) => (
  <div className={`inline-flex items-center gap-2 ${className || ''}`} {...props}>
    {children}
  </div>
);

/* ============ Table (sederhana) ============ */
export const Table = ListTable;

/* ============ Menu ============ */
const MenuCtx = createContext({ open: false, setOpen: () => {}, placement: 'bottom-start' });

export const Menu = ({
  children,
  className,
  placement = 'bottom-start',
  animate: _animate,
  ...props
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);
  return (
    <MenuCtx.Provider value={{ open, setOpen, placement }}>
      <div ref={ref} className={`relative inline-block ${className || ''}`} {...props}>
        {children}
      </div>
    </MenuCtx.Provider>
  );
};

export const MenuHandler = ({ children, ...props }) => {
  const { setOpen } = useContext(MenuCtx);
  const child = React.Children.only(children);
  return React.cloneElement(child, {
    ...props,
    onClick: (e) => {
      setOpen((o) => !o);
      child.props.onClick?.(e);
    },
  });
};

export const MenuList = ({ className, children, ...props }) => {
  const { open, placement } = useContext(MenuCtx);
  if (!open) return null;
  const pos =
    placement === 'bottom-end' || placement === 'top-end'
      ? 'right-0'
      : placement === 'bottom-start' || placement === 'top-start'
        ? 'left-0'
        : '';
  const vert = placement?.startsWith('top') ? 'bottom-full mb-2' : 'top-full mt-2';
  return (
    <div
      className={`absolute z-50 ${pos} ${vert} min-w-[10rem] rounded-lg border border-gray-200 bg-white py-1 shadow-lg ${className || ''}`}
      {...props}
    >
      {children}
    </div>
  );
};

export const MenuItem = ({ className, children, ...props }) => {
  const { setOpen } = useContext(MenuCtx);
  return (
    <button
      type='button'
      className={`block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 ${className || ''}`}
      onClick={(e) => {
        props.onClick?.(e);
        setOpen(false);
      }}
      {...props}
    >
      {children}
    </button>
  );
};

/* ============ Tabs (value-based, meniru API material-tailwind) ============ */
const TabsCtx = createContext({ value: null, setValue: () => {} });

export const Tabs = ({ value, className, children, ...props }) => {
  const [inner, setInner] = useState(value);
  const active = value !== undefined ? value : inner;
  return (
    <TabsCtx.Provider value={{ value: active, setValue: setInner }}>
      <div className={className} {...props}>
        {children}
      </div>
    </TabsCtx.Provider>
  );
};

export const TabsHeader = ({ className, children, ...props }) => (
  <div className={`flex gap-2 ${className || ''}`} {...props}>
    {children}
  </div>
);

export const Tab = ({ value, className, children, onClick, ...props }) => {
  const { value: active, setValue } = useContext(TabsCtx);
  const isActive = active === value;
  return (
    <button
      type='button'
      className={`rounded-lg px-4 py-2 text-sm transition-colors ${isActive ? 'bg-primary text-white' : 'text-gray-600 hover:text-primary'} ${className || ''}`}
      onClick={(e) => {
        setValue(value);
        onClick?.(e);
      }}
      {...props}
    >
      {children}
    </button>
  );
};

export const TabsBody = ({ className, children, ...props }) => (
  <div className={className} {...props}>
    {children}
  </div>
);

export const TabPanel = ({ value, className, children, ...props }) => {
  const { value: active } = useContext(TabsCtx);
  if (active !== value) return null;
  return (
    <div className={className} {...props}>
      {children}
    </div>
  );
};
